import {connect} from "react-redux";
import {Box, Button, Card, CardContent, List, ListItemButton, ListItemText, Typography} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, {useCallback, useContext, useEffect, useState} from "react";
import {PathwayUser, ProjectDetails, ProjectRoles} from "../../redux/types/firebase";
import {updateMode, updateProject} from "../../redux/slices/editor";
import {selectProjects, selectUser} from "../../redux/selectors/firebase";
import {selectProject} from "../../redux/selectors/editor";
import ProjectDetailsEditor from "./ProjectDetailsEditor";
import ProjectDetailsViewer from "./ProjectDetailsViewer";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import ImportProjectDialog from "./ImportProjectDialog";

export const ProjectTables = ["configs", "comments", "constants", "dictionaries", "grammar", "localization", "locks",
	"networks", "nodes", "projects", "revisions", "sheets", "tags", "variables", "versions"];

const useStyles = makeStyles(theme => ({
	card: {
		display: 'flex',
		justifyContent: "center",
		margin: theme.spacing(8),
		padding: theme.spacing(2),
		position: "fixed",
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		flexGrow: 1,
		flexDirection: 'column',
		[theme.breakpoints.only("sm")]: {
			margin: theme.spacing(2)
		},

	},
	content: {
		display: "flex",
		flexDirection: "column",
		height: "100%",
		overflow: "hidden"
	},
	details: {
		display: "flex",
		flexDirection: "column",
		flexGrow: 1
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		marginBottom: theme.spacing(1)
	},
	list: {
		height: "100%",
		overflowY: "scroll",
		margin: theme.spacing(1),
		'&::-webkit-scrollbar': {
			width: '0.4em'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: 'rgba(0,0,0,.1)',
			borderRadius: '0.4em'
		}
	},
	projects: {
		overflowY: "auto"
	},
	select: {
		display: "flex",
		flexGrow: 1,
		height: "100%"
	}
}));

type Props = {
    projects: {[key: string]: ProjectDetails}
    updateProject: (project: string) => any
    updateMode: (mode: string) => any
    user: PathwayUser
}
function Projects({projects, updateProject, updateMode, user}: Props) {
	const classes = useStyles();
	const hasReadAccess = useCallback((project: string) => {
		const projectRoles = (user.roles as ProjectRoles)[project];

		return user.roles.admin || user.roles.editor || user.roles.viewer ||
			projectRoles?.viewer || projectRoles?.editor;
	}, [user]);
	const [showImport, setShowImport] = useState(false);
	const [selectedProject, setSelectedProject] = useState<string>();
	const [visibleProjects, setVisibleProjects] = useState<string[]>([]);
	const {push, pushState, setNetworkMember, setProjectMember, track} = useContext(FirebaseContext);
	const handleSetMode = (mode: string, project: string) => {
		updateProject(project);
		updateMode(mode);
	};
	const handleClickAddProject = () => {
		push("projects", {
			audioSource: "https://content.flavourworks.co/REPLACE/audio",
			imageSource: "https://content.flavourworks.co/REPLACE/image",
			name: "New Project",
			styleSource: "https://content.flavourworks.co/REPLACE/style",
			videoSource: "https://content.flavourworks.co/REPLACE/video",
		}).then(project => {
			if (project) {
				pushState(project, {name: "Initial State"}).then(state => {
					if (state) {
						setNetworkMember(project, "initial", state);
					}
				})
			}
		});
	}
	const handleClickImportProject = () => setShowImport(true);
	const handleCloseImportProject = () => setShowImport(false);
	const handleProjectClick = (key: string) => () => setSelectedProject(key);
	const onUpdate = (project: string, field: string, value: unknown) => setProjectMember(project, field, value);
	const projectRoles = user.roles as ProjectRoles;
	const editor = selectedProject && (user.roles.editor || projectRoles[selectedProject]?.editor);

	useEffect(() => {
		if (user.roles.viewer) {
			track("projects");
		} else {
			Object.keys(user.roles).forEach(key => {
				const projectRoles = (user.roles as ProjectRoles)[key];

				if (projectRoles.viewer) {
					track(`projects/${key}`, ["projects", key]);
				}
			});
		}
	}, [track, user]);
	useEffect(() => {
		setVisibleProjects(Object.keys(projects || {}).filter(key => hasReadAccess(key) && projects[key]?.name)
			.sort((lhs, rhs) => projects[lhs].name.localeCompare(projects[rhs].name)));
	}, [hasReadAccess, projects])
	useEffect(() => {
		if (selectedProject === undefined && visibleProjects.length) {
			setSelectedProject(visibleProjects[0]);
		}
	}, [selectedProject, visibleProjects])
	if (! projects) return null;
	return (
		<>
			<Card className={classes.card}>
				<CardContent className={classes.content}>
					<Box className={classes.header}>
						<Typography variant="h5">
							Select a project to continue
						</Typography>
						<Box>
							{user.roles.admin &&
								<Button onClick={handleClickImportProject} title="Import a project" variant="contained"
										sx={{m:1}}>
									Import Project
								</Button>}
							{user.roles.editor &&
								<Button onClick={handleClickAddProject} title="Add new project" variant="contained"
										sx={{m:1}}>
									Add Project
								</Button>}
						</Box>
					</Box>
					<Box className={classes.select}>
						<Box className={classes.list}>
							<List className={classes.projects}>
								{visibleProjects.map(key =>
										<ListItemButton key={key} onClick={handleProjectClick(key)}
														selected={selectedProject === key}>
											<ListItemText primary={projects[key]?.name}/>
										</ListItemButton>
									)}
							</List>
						</Box>
						<Box className={classes.details}>
							{selectedProject && editor &&
								<ProjectDetailsEditor onSetMode={handleSetMode} onUpdate={onUpdate}
													  project={selectedProject} projects={projects} user={user}/>
							}
							{selectedProject && ! editor &&
								<ProjectDetailsViewer onSetMode={handleSetMode} project={selectedProject}
													  projects={projects}/>
							}
						</Box>
					</Box>
				</CardContent>
			</Card>
			{showImport && <ImportProjectDialog onClose={handleCloseImportProject}/>}
		</>
    );
}
const mapStateToProps = (store: never) => ({
	project: selectProject(store),
	projects: selectProjects(store) || {},
    user: selectUser(store),
});

export default connect(mapStateToProps, {
    updateMode,
    updateProject,
})(Projects);

